// src/modules/gyms/components/branch-page/BranchDetailHeader.tsx
import { Dropdown } from "react-bootstrap";
import { formatApiDateTime } from "@modules/shared/utils/handle-date.utils";
import { getTodayHours, isCurrentlyOpen } from "../../utils/time.utils";

interface BranchDetailHeaderProps {
  name: string;
  address: string;
  bannerUrl: string;
  logoUrl: string;
  status: boolean;
  statusLabel?: string;
  operatingHoursFrom: string;
  operatingHoursTo: string;
  openingHours?: Record<string, { start_time: string; end_time: string }>;
  categories?: string[];
  description?: string;
  ctaLink?: string;
  updatedAt?: string;
  brandName?: string;
  isActive: boolean;
  onToggleStatus: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage: boolean;
}

// Hàm kiểm tra ngày nghỉ (00:00-00:00)
const isDayClosed = (
  openingHours?: Record<string, { start_time: string; end_time: string }>,
  dayKey?: string,
): boolean => {
  if (!openingHours || !dayKey) return false;
  const day = openingHours[dayKey];
  if (!day) return false;
  return day.start_time === "00:00" && day.end_time === "00:00";
};

export const BranchDetailHeader = ({
  name,
  address,
  bannerUrl,
  logoUrl,
  status,
  statusLabel,
  operatingHoursFrom,
  operatingHoursTo,
  openingHours,
  categories = [],
  description,
  ctaLink,
  updatedAt,
  brandName,
  isActive,
  onToggleStatus,
  onEdit,
  onDelete,
  canManage,
}: BranchDetailHeaderProps) => {
  const isOpen = status;
  const todayHours = getTodayHours(
    openingHours,
    operatingHoursFrom,
    operatingHoursTo,
  );
  const todayClosed = isDayClosed(
    openingHours,
    new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
  );
  const isOpenNow =
    !todayClosed && isCurrentlyOpen(todayHours.from, todayHours.to);

  return (
    <div className="card border-0 shadow-sm overflow-hidden">
      {/* Banner */}
      <div className="position-relative" style={{ height: 340 }}>
        <img
          src={bannerUrl}
          alt={`Banner của ${name}`}
          className="w-100 h-100"
          style={{ objectFit: "cover" }}
        />
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.55) 85%, rgba(0,0,0,0.65) 100%)",
          }}
        />

        {/* Dropdown hành động */}
        {canManage && (
          <div
            className="position-absolute top-0 end-0 m-3"
            style={{ zIndex: 10 }}
          >
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                className="rounded-circle p-2 border-0 bg-white bg-opacity-75 backdrop-blur-sm d-flex align-items-center justify-content-center"
                id="branch-actions-dropdown"
                style={{ width: 40, height: 40 }}
              >
                <i
                  className="bi bi-three-dots-vertical"
                  style={{ fontSize: "1.4rem", lineHeight: 1 }}
                />
              </Dropdown.Toggle>

              <Dropdown.Menu
                align="end"
                className="shadow-sm border-0 py-2 rounded-3"
                style={{ minWidth: 220, fontSize: "1rem" }}
              >
                <Dropdown.Item
                  onClick={onEdit}
                  className="d-flex align-items-center gap-3 px-4 py-3 hover-bg-light"
                >
                  <span className="fw-medium">Chỉnh sửa chi nhánh</span>
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={onToggleStatus}
                  className="d-flex align-items-center gap-3 px-4 py-3 hover-bg-light"
                >
                  <span className="fw-medium">
                    {isActive ? "Đóng cửa" : "Mở cửa"}
                  </span>
                </Dropdown.Item>

                <Dropdown.Divider className="my-1" />

                <Dropdown.Item
                  onClick={onDelete}
                  className="d-flex align-items-center gap-3 px-4 py-3 text-danger hover-bg-danger-light"
                >
                  <span className="fw-medium">Xóa chi nhánh</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        {/* Thông tin chính dưới banner */}
        <div className="position-absolute bottom-0 start-0 w-100 p-4 text-white">
          <div className="d-flex align-items-end gap-3">
            <img
              src={logoUrl}
              alt={name}
              className="rounded-circle border border-3 border-white bg-white"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
              }}
            />
            <div className="flex-grow-1">
              {/* {brandName && (
                <div
                  className="text-white-50 small text-uppercase tracking-wide"
                  style={{ fontSize: "0.75rem" }}
                >
                  {brandName}
                </div>
              )} */}
              <h3
                className="mb-0 text-white fw-bold"
                style={{ fontSize: "1.15rem" }}
              >
                {name}
              </h3>
              <div className="gap-2">
                <span
                  className="d-flex align-items-center gap-1 text-white-50"
                  style={{ fontSize: "1rem" }}
                >
                  <i className="bi bi-geo-alt" />
                  {address}
                </span>
                <span
                  className={`mt-2 badge px-3 py-2 rounded-pill ${isOpen ? "bg-success" : "bg-secondary"}`}
                  style={{ fontSize: "0.75rem", fontWeight: 600 }}
                >
                  <i className="bi bi-check-circle-fill me-1" />
                  {statusLabel || (isOpen ? "Hoạt động" : "Tạm ngưng")}
                </span>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-3 p-3 border border-white border-opacity-10 d-none d-sm-block">
              <div
                className="d-flex flex-column gap-1 text-white"
                style={{ fontSize: "0.9rem" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-clock" />
                  {todayClosed ? (
                    <span
                      className="ms-2 badge bg-secondary rounded-pill px-3 py-3"
                      style={{ fontSize: "0.85rem", fontWeight: 600 }}
                    >
                      <i className="bi bi-x-circle-fill me-1" />
                      Nghỉ
                    </span>
                  ) : (
                    <span style={{ fontSize: "1rem", fontWeight: 400 }}>
                      {todayHours.from} – {todayHours.to}
                      <span
                        className={`ms-2 badge rounded-pill px-2 ${
                          isOpenNow ? "bg-success" : "bg-secondary"
                        }`}
                        style={{ fontSize: "0.75rem", fontWeight: 600 }}
                      >
                        {isOpenNow ? "Đang mở" : "Đã đóng"}
                      </span>
                    </span>
                  )}
                </div>
                {categories.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-tag" />
                    <span>{categories.join(" · ")}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="card-body">
        {description && (
          <div className="d-flex gap-2 p-3 bg-light rounded-3 mb-3">
            <i className="bi bi-info-circle text-secondary flex-shrink-0 mt-1" />
            <div>
              <div
                className="text-uppercase small text-muted fw-semibold tracking-wide"
                style={{ fontSize: "0.75rem" }}
              >
                Mô tả
              </div>
              <p
                className="mb-0 text-secondary"
                style={{ fontSize: "0.95rem" }}
              >
                {description}
              </p>
            </div>
          </div>
        )}
        {ctaLink && (
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <span
              className="text-uppercase small text-muted fw-semibold tracking-wide"
              style={{ fontSize: "0.75rem" }}
            >
              Liên kết CTA
            </span>
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-1"
              style={{ fontSize: "0.9rem" }}
            >
              <span>{ctaLink}</span>
              <i className="bi bi-box-arrow-up-right" />
            </a>
          </div>
        )}
        {updatedAt && (
          <div
            className="text-muted small border-top pt-2"
            style={{ fontSize: "0.875rem" }}
          >
            Cập nhật lần cuối: {formatApiDateTime(updatedAt)}
          </div>
        )}
      </div>

      <style>
        {`
          .tracking-wide { letter-spacing: 0.04em; }
          .backdrop-blur-sm { backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
          .hover-bg-light:hover { background-color: var(--bs-light); }
          .hover-bg-danger-light:hover { background-color: #f8d7da; }
        `}
      </style>
    </div>
  );
};
